import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Plus, Check, Loader2, Video, X, Music } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackSearch = ({ onAddTrack, existingTrackIds = [], placeholder = "Search for songs...", allowVideosToggle = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [includeVideos, setIncludeVideos] = useState(false);

  const search = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const params = { q: query, limit: 10 };
      if (allowVideosToggle && includeVideos) params.include_videos = true;
      const response = await axios.get(`${API}/music/search`, { params });
      setResults(response.data.tracks || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, [query, allowVideosToggle, includeVideos]);

  const clearAll = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') search();
  };

  return (
    <div data-testid="track-search">
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(v);
              // Clearing the box should clear the results — they shouldn't
              // linger under an empty search field.
              if (!v.trim()) setResults([]);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pl-9 pr-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="track-search-input"
          />
          {(query || results.length > 0) && (
            <button
              type="button"
              onClick={clearAll}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-0.5 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Clear search"
              title="Clear search & results"
              data-testid="track-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button
          onClick={search}
          disabled={!query.trim() || searching}
          className="bg-[#FC3C44] hover:bg-[#E0353D] text-white"
          data-testid="track-search-button"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
        </Button>
      </div>

      {allowVideosToggle && (
        <div className="flex items-center gap-2 mb-3 -mt-2">
          <button
            type="button"
            onClick={() => setIncludeVideos((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors border ${
              includeVideos
                ? 'bg-[#FF007F]/15 text-[#FF007F] border-[#FF007F]/40'
                : 'bg-white/5 text-gray-400 border-white/10 hover:text-white'
            }`}
            data-testid="track-search-include-videos-toggle"
            title="Also return DJ remixes, covers, and lyric videos (non-ATV uploads)"
          >
            <Video className="w-3 h-3" />
            {includeVideos ? 'Videos on' : 'Include videos'}
          </button>
          {includeVideos && (
            <span className="text-[10px] text-gray-500">Songs, remixes, covers, lyric videos</span>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className="max-h-80 overflow-y-auto -mx-1 px-1 divide-y divide-white/[0.06]">
          {results.map((track) => {
            const isAdded = existingTrackIds.includes(track.id);
            const artists = Array.isArray(track.artists) ? track.artists.join(', ') : track.artists;
            return (
              <div key={track.id} className="flex items-center gap-4 py-3" data-testid={`search-result-${track.id}`}>
                {track.album_image ? (
                  <img src={track.album_image} alt={track.album} className="w-14 h-14 rounded-full object-cover flex-shrink-0 ring-1 ring-white/10" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 ring-1 ring-white/10">
                    <Music className="w-5 h-5 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[15px] font-semibold truncate leading-tight">{track.name}</p>
                  <p className="text-gray-400 text-sm truncate mt-0.5">{artists}</p>
                </div>
                <Button
                  onClick={() => {
                    onAddTrack(track);
                    // Per operator request: once a selection is made, clear
                    // the search box AND the results so nothing lingers.
                    clearAll();
                  }}
                  disabled={isAdded}
                  className={`h-10 px-5 rounded-full font-bold text-sm flex items-center gap-1.5 flex-shrink-0 transition-all ${
                    isAdded
                      ? 'bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/15'
                      : 'bg-gradient-to-b from-[#FF4A52] to-[#E0353D] hover:from-[#FF5A62] hover:to-[#FC3C44] text-white shadow-lg shadow-[#FC3C44]/25'
                  }`}
                  data-testid={`add-track-${track.id}`}
                >
                  {isAdded ? <><Check className="w-4 h-4" strokeWidth={2.5} /> Added</> : <><Plus className="w-4 h-4" strokeWidth={2.5} /> Add</>}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrackSearch;
